import get from 'lodash.get';
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';
import orderBy from 'lodash.orderby';
import { AppState } from 'services/types';
import {
    SprayEntityIds,
    SprayEventTotalArea,
    SprayEvent,
    SprayEventUuid,
    SprayApplicationRate,
    SprayEventApplicationType,
    SprayEventPostRequestPayload,
    SprayEventPatchRequestPayload
} from 'services/sprayEvents/types';
import { selectEntities } from 'services/entities/selectors';
import { selectSprayEvents } from 'services/sprayEvents/selectors';
import { selectOptions as selectWaterSourceOptions } from 'components/WaterSourceDropdown/selectors';
import { makeSelectQueryStringValue } from 'workspaces/react-router-selectors';
import { COPY_SPRAY_EVENT_MODAL } from 'scenes/Spray/scenes/Diary/constants';
import { Calibration, EquipmentSetId } from 'services/equipmentSets/types';
import { makeSelectCalibrations } from 'services/equipmentSets/selectors';
import {
    EntityRowSpacing,
    EntityWithComputedFields
} from 'services/entities/types';
import { mapSprayEventFromPayload } from 'services/sprayEvents/utils';
import { OwnProps } from './index';
import { SprayEventInitialValues } from './types';

type Props = {
    entityIds: SprayEntityIds;
    equipmentSetId?: EquipmentSetId;
    applicationRate?: SprayApplicationRate;
};

const selectEntityIds = (state: AppState, props: Props): SprayEntityIds =>
    props.entityIds;

const selectSprayEventUuid = (
    state: AppState,
    props: OwnProps
): SprayEventUuid | undefined => props.sprayEventUuid;

// Get an entity's effective useable area, including subtracted removals
const getEffectiveArea = (entity: EntityWithComputedFields) => {
    const totalArea = get(entity, 'totalArea', 0);
    const removedArea = get(entity, 'removedTotalArea', 0);

    return Number(totalArea) - Number(removedArea);
};

const selectTotalSprayedArea = createDeepEqualSelector(
    selectEntityIds,
    selectEntities,
    makeSelectCalibrations(),
    (entityIds, entities, calibrations: Calibration[]) => {
        if (entityIds && entityIds.length) {
            return entityIds.reduce((acc, cur): SprayEventTotalArea => {
                const effectiveArea = getEffectiveArea(get(entities, cur));
                const rowSpacing: EntityRowSpacing = get(
                    entities,
                    [cur, 'rowSpacing'],
                    null
                );

                if (rowSpacing) {
                    const calibration = calibrations.find((value) => {
                        return (
                            rowSpacing >= value.rowSpacingMin &&
                            rowSpacing <= value.rowSpacingMax
                        );
                    });

                    if (
                        calibration &&
                        calibration.swathWidth &&
                        effectiveArea
                    ) {
                        const blockSprayedArea = Number(
                            (calibration.swathWidth / rowSpacing) *
                                effectiveArea
                        );

                        return acc + blockSprayedArea;
                    }
                }

                return acc;
            }, 0);
        }

        return 0;
    }
);

const selectTotalArea = createDeepEqualSelector(
    selectEntityIds,
    selectEntities,
    (entityIds, entities) => {
        if (entityIds && entityIds.length) {
            return entityIds.reduce((acc, cur): SprayEventTotalArea => {
                const effectiveArea = getEffectiveArea(get(entities, cur));

                if (effectiveArea) {
                    return Number((acc + effectiveArea).toFixed(2));
                }

                return Number(acc.toFixed(2));
            }, 0);
        }

        return 0;
    }
);

const selectEffectiveRatio = createDeepEqualSelector(
    selectTotalArea,
    selectTotalSprayedArea,
    (totalArea, totalSprayedArea) => totalSprayedArea / totalArea
);

const selectSprayEvent = createDeepEqualSelector(
    [selectSprayEvents, selectSprayEventUuid],
    (sprayEventsState, sprayEventUuidProp): SprayEvent | undefined => {
        if (sprayEventUuidProp) {
            return get(sprayEventsState, sprayEventUuidProp);
        }

        return undefined;
    }
);

const selectSprayEventIsCompleted = createDeepEqualSelector(
    selectSprayEvent,
    (sprayEvent) => {
        if (!sprayEvent) {
            return false;
        }

        const completedAt = get(sprayEvent, ['jobs', 0, 'completedAt']);

        return Boolean(completedAt);
    }
);

const makeSelectSprayEventIsCompleted =
    (): typeof selectSprayEventIsCompleted => selectSprayEventIsCompleted;

const selectInitialValues = createDeepEqualSelector(
    selectSprayEvent,
    selectWaterSourceOptions,
    makeSelectQueryStringValue(COPY_SPRAY_EVENT_MODAL),
    makeSelectQueryStringValue('sprayEventUuid'),
    (
        event,
        waterSourceOptions,
        isCopySprayModalOpen,
        selectedSprayEventUuid
    ) => {
        const defaultWaterSourceId =
            waterSourceOptions.length === 1
                ? waterSourceOptions[0].value
                : null;
        let initialValues: SprayEventInitialValues = {
            sprayProducts: [{}],
            applicationType: SprayEventApplicationType.STANDARD
        };

        if (defaultWaterSourceId) {
            initialValues.sprayWaterSourceId = defaultWaterSourceId;
        }

        if (!event || !event.sprayEvent) {
            return initialValues;
        }

        const { sprayEvent, entityId, startDate } = event;
        const {
            sprayEntities,
            sprayMethod,
            sprayWaterSourceId,
            justification,
            technicalPersonName,
            isFinalSpray,
            applicationRate: sprayApplicationRate,
            applicationType,
            sprayEventOperations
        } = sprayEvent;

        const sprayEntityIds = sprayEntities.map(({ id }) => id);

        const sprayProducts = orderBy(sprayEvent.sprayProducts, 'orderIndex');

        const isCopyingExistingSprayEvent =
            isCopySprayModalOpen && selectedSprayEventUuid;

        initialValues = {
            sprayEntityIds: isCopyingExistingSprayEvent
                ? undefined
                : sprayEntityIds,
            entityId: isCopyingExistingSprayEvent ? undefined : entityId,
            startDate,
            sprayMethod,
            applicationType:
                applicationType ?? SprayEventApplicationType.STANDARD,
            sprayEventOperations,
            sprayApplicationRate,
            sprayWaterSourceId,
            sprayProducts,
            justification,
            technicalPersonName,
            isFinalSpray
        };

        return mapSprayEventFromPayload(
            initialValues as
                | SprayEventPostRequestPayload
                | SprayEventPatchRequestPayload
        );
    }
);

const makeSelectInitialValues = (): typeof selectInitialValues =>
    selectInitialValues;

export {
    selectEntityIds,
    selectTotalArea,
    makeSelectInitialValues,
    selectSprayEventUuid,
    selectSprayEvent,
    makeSelectSprayEventIsCompleted,
    selectTotalSprayedArea,
    selectEffectiveRatio
};
